import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTable from '../../components/common/DataTable';
import { useOrderSchedules, useDeleteOrderSchedule } from '../../hooks/useApi';
import type { OrderSchedule, OrderScheduleStatus, RecurrenceType } from '../../types';

const statusConfig: Record<OrderScheduleStatus, { label: string; color: 'default' | 'info' | 'success' | 'warning' }> = {
  ACTIVE: { label: 'Aktiv', color: 'success' },
  PAUSED: { label: 'Pauseret', color: 'warning' },
  COMPLETED: { label: 'Afsluttet', color: 'default' },
};

const recurrenceLabels: Record<RecurrenceType, string> = {
  DAILY: 'Dagligt',
  WEEKLY: 'Ugentligt',
  BIWEEKLY: 'Hver 2. uge',
  EVERY_X_DAYS: 'Hver X dage',
  SPECIFIC_WEEKDAYS: 'Bestemte ugedage',
};

const weekdayNames = ['', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];

function fmtDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function recurrenceText(row: OrderSchedule): string {
  if (row.recurrenceType === 'EVERY_X_DAYS') {
    return `Hver ${row.intervalDays}. dag`;
  }
  if (row.recurrenceType === 'SPECIFIC_WEEKDAYS') {
    const days = (row.weekdays || []).map(d => weekdayNames[d] || d).join(', ');
    return days || 'Bestemte ugedage';
  }
  return recurrenceLabels[row.recurrenceType] || row.recurrenceType;
}

export default function OrderScheduleList() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const { data: schedules = [], isLoading } = useOrderSchedules(statusFilter ? { status: statusFilter } : undefined);
  const deleteSchedule = useDeleteOrderSchedule();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const deleteName = schedules.find(s => s.id === deleteId)?.name || '';

  async function handleDelete() {
    if (!deleteId) return;
    try {
      await deleteSchedule.mutateAsync(deleteId);
      setSnackbar({ open: true, message: 'Ordreskema slettet', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke slette ordreskema', severity: 'error' });
    }
    setDeleteId(null);
  }

  const columns = [
    {
      id: 'name' as const,
      label: 'Navn',
      minWidth: 160,
    },
    {
      id: 'status' as const,
      label: 'Status',
      minWidth: 90,
      format: (value: unknown) => {
        const c = statusConfig[value as OrderScheduleStatus] || { label: value, color: 'default' as const };
        return <Chip label={c.label} color={c.color} size="small" />;
      },
    },
    {
      id: 'recurrenceType' as const,
      label: 'Gentagelse',
      minWidth: 120,
      format: (_: unknown, row: OrderSchedule) => recurrenceText(row),
    },
    {
      id: 'startDate' as const,
      label: 'Periode',
      minWidth: 140,
      format: (_: unknown, row: OrderSchedule) =>
        `${fmtDate(row.startDate)} – ${fmtDate(row.endDate)}`,
    },
    {
      id: '_count' as const,
      label: 'Skabeloner',
      minWidth: 90,
      align: 'right' as const,
      format: (value: unknown) => {
        const count = (value as any)?.orderTemplates ?? 0;
        return `${count}`;
      },
    },
    {
      id: 'id' as const,
      label: '',
      minWidth: 50,
      sortable: false,
      format: (_: unknown, row: OrderSchedule) => (
        <IconButton
          size="small"
          color="error"
          onClick={(e) => { e.stopPropagation(); setDeleteId(row.id); }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" fontWeight={600}>Ordreskemaer</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/ordreskemaer/ny')}>
          Nyt ordreskema
        </Button>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <ToggleButtonGroup value={statusFilter} exclusive onChange={(_, val) => setStatusFilter(val || '')} size="small">
          <ToggleButton value="">Alle</ToggleButton>
          <ToggleButton value="ACTIVE">Aktive</ToggleButton>
          <ToggleButton value="PAUSED">Pauserede</ToggleButton>
          <ToggleButton value="COMPLETED">Afsluttede</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isLoading ? (
        <Typography>Indlæser...</Typography>
      ) : (
        <DataTable
          columns={columns}
          data={schedules}
          rowKey="id"
          onRowClick={(row) => navigate(`/ordreskemaer/${row.id}`)}
          searchable
          searchPlaceholder="Søg ordreskemaer..."
        />
      )}

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Slet ordreskema</DialogTitle>
        <DialogContent><DialogContentText>Er du sikker på du vil slette &quot;{deleteName}&quot;? Allerede oprettede ordrer i DORA påvirkes ikke.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Annuller</Button>
          <Button color="error" onClick={handleDelete} disabled={deleteSchedule.isPending}>Slet</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
